import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import {
  useCreatePolicyCoverageMutation,
  useGetCustomerQuery,
  useGetProductBrandQuery,
  useGetCustomerRelationQuery,
  useGetAllPolicyGroupByCountQuery,
} from "../../../Api/attoDeskApi";
import {
  CustomerOption,
  CustomerOptionString,
  ICustomer,
  ICustomerRelative2,
  IProductBrand,
  policyTermsArray,
} from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import { useFormik } from "formik";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DaysOfMonth, PolicyCollectionType } from "../../../Core/Enum/enum";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import NewProductBrand from "../../ProductBrand/Edit/index";
import AvatarImg from "../../../Images/avatar.png";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import NewPopUpRelative from "../../Customer/Edit/relative";
import { BiSolidMessageSquareAdd } from "react-icons/bi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

interface PolicyCustomerMember {
  customer: ICustomer;
  relationship: string;
}

function getPolicyCollectionType(policyPremiumFrequency: string) {
  switch (policyPremiumFrequency) {
    case PolicyCollectionType.OneTime:
      return "One Time";
    case PolicyCollectionType.Monthly:
      return "Monthly";
    case PolicyCollectionType.EveryTwoMonths:
      return "Every Two Months";
    case PolicyCollectionType.EveryThreeMonths:
      return "Every Three Months";
    case PolicyCollectionType.Quarterly:
      return "Quarterly";
    case PolicyCollectionType.SemiAnnually:
      return "Semi-Annually";
    case PolicyCollectionType.Annually:
      return "Annually";
    case PolicyCollectionType.Custom:
      return "Custom";
    default:
      return "";
  }
}

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id: customerId } = useParams<{ id: string }>();
  const { showErrorMessage, showMessage } = useNotifier();

  useEffect(() => {}, [customerId]);

  const { data, isLoading, isError } = useGetCustomerQuery({
    searchText: "",
    id: String(customerId),
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [addedCustomers, setAddedCustomers] = useState<PolicyCustomerMember[]>(
    []
  );
  const [openProductBrand, setOpenProductBrand] = useState(false);
  const [currentTerm, setCurrentTerm] = useState<policyTermsArray[]>([]);
  const [openAddProject, setOpenAddProject] = useState(false);

  const { data: relationData, isLoading: relationIsLoading } =
    useGetCustomerRelationQuery({
      policyCustomerId: String(customerId),
    });

  const [newPolicy, { isLoading: dataIsLoading }] =
    useCreatePolicyCoverageMutation();

  const {
    data: countData,
    isLoading: countDataIsLoading,
    refetch: countRefetch,
    isError: countIsError,
  } = useGetAllPolicyGroupByCountQuery({
    searchText: "",
    id: "",
    policyCustomerId: String(customerId),
  });

  const customer = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as ICustomer;
    }
    return null;
  }, [data]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    number | null
  >(null);

  const [selectedSubToSubCategoryId, setSelectedSubToSubCategoryId] = useState<
    number | null
  >(null);

  const [selectedSubToSubToSubCategoryId, setSelectedSubToSubToSubCategoryId] =
    useState<number | null>(null);

  const relationDataList = useMemo(() => {
    return (relationData?.data ?? []) as ICustomerRelative2[];
  }, [relationData?.data]);

  const relationOptions = useMemo(() => {
    if (!Array.isArray(relationDataList)) return [];
    return relationDataList.map((cust) => ({
      label: `${toProperCase(cust?.related_customer_name)} - ${
        cust.relationship_type
      }`,
      value: cust.id,
    }));
  }, [relationDataList]);

  const { data: brandListData, isLoading: brandIsLoading } =
    useGetProductBrandQuery({
      searchText: "",
      isActive: true,
    });

  const brandList = useMemo(() => {
    return brandListData?.data as IProductBrand[];
  }, [brandListData?.data]);

  const brandOptions = useMemo(() => {
    if (!Array.isArray(brandList)) return [];
    return brandList.map((cust) => ({
      label: `${toProperCase(cust.productBrandName)}`,
      value: cust.id,
    }));
  }, [brandList]);

  const dayOptions: CustomerOptionString[] = Object.values(DaysOfMonth).map(
    (day) => ({
      label: day,
      value: day,
    })
  );

  const premiumFrequencyOptions: CustomerOptionString[] = Object.values(
    PolicyCollectionType
  ).map((day) => ({
    label: getPolicyCollectionType(day),
    value: day,
  }));

  const today = dayjs();

  const handleOpenDialogRelative = () => {
    setOpenAddProject(true);
  };

  const handleCloseDialogRelative = () => {
    setOpenAddProject(false);
  };

  const nextOrderNumber = Number(countData?.data || 0) + 1;

  const formik = useFormik({
    initialValues: {
      policyCustomerId: customerId,
      jointPolicyOwnerId: "",
      contengentPolicyOwnerId: "",
      policyNumber: "",
      policyCategoryId: "",
      mainCatId: "",
      policyInsuranceCompanyId: "",
      policyProductId: "",
      policyIssueDate: null,
      policyIsLifeTimeExpiryDate: false,
      policyExpiryDate: null,
      policyCoversionDate: null,
      policyRenewalDate: null,
      policyPaidUpDate: null,
      policyPayor: "",
      policyPremiumDetails: "",
      policyPremium: "",
      policyCoverageAmount: "",
      policyPremiumFrequency: "",
      policyDrawDay: "",
      policyInsuredDetails: [],
      policyPrimaryBenificaries: "",
      policyContengentBenificaries: "",
      policySummaryReportArray: [],
      policyOfficeUse: "",
      policyGtRefID: "",
      policyRemark: "",
      policyPremiumFrequencyDate: null,
      policyOrderNumber: nextOrderNumber,
      coverageOrderNumber: 1,
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const policyCategoryId = selectedSubToSubToSubCategoryId
          ? Number(selectedSubToSubToSubCategoryId)
          : selectedSubToSubCategoryId
          ? Number(selectedSubToSubCategoryId)
          : selectedSubCategoryId
          ? Number(selectedSubCategoryId)
          : selectedCategoryId
          ? Number(selectedCategoryId)
          : String(values.mainCatId);

        const resolvedPolicyCategoryId = selectedId
          ? Number(values.policyCategoryId)
          : policyCategoryId;

        const temData = {
          policyCustomerId: values.policyCustomerId,
          contengentPolicyOwnerId: values.contengentPolicyOwnerId,
          jointPolicyOwnerId: values.jointPolicyOwnerId,
          policyNumber: values.policyNumber,
          policyCategoryId: resolvedPolicyCategoryId,
          policyInsuranceCompanyId: values.policyInsuranceCompanyId,
          policyProductId: values.policyProductId,
          policyIssueDate: values.policyIssueDate,
          policyExpiryDate: values.policyExpiryDate,
          policyIsLifeTimeExpiryDate: values.policyIsLifeTimeExpiryDate,
          policyCoversionDate: values.policyCoversionDate,
          policyRenewalDate: values.policyRenewalDate,
          policyPaidUpDate: values.policyPaidUpDate,
          policyPayor: toProperCase(values.policyPayor),
          // policyPremiumDetails: values.policyPremiumDetails,
          // policyPremium: String(values.policyPremium),
          // policyCoverageAmount: values.policyCoverageAmount,
          policyPremiumFrequency: values.policyPremiumFrequency,
          policyPremium: values.policyPremium
            ? String(values.policyPremium)
            : "",
          policyCoverageAmount: values.policyCoverageAmount
            ? String(values.policyCoverageAmount)
            : "",
          policyPremiumDetails: values.policyPremiumDetails
            ? String(values.policyPremiumDetails)
            : "",

          policyDrawDay: values.policyDrawDay,
          policyInsuredDetails: addedCustomers,
          policyPrimaryBenificaries: values.policyPrimaryBenificaries,
          policyContengentBenificaries: values.policyContengentBenificaries,
          policyOfficeUse: values.policyOfficeUse,
          policyGtRefID: values.policyGtRefID,
          policySummaryReportArray: currentTerm,
          policyRemark: values.policyRemark,
          policyPremiumFrequencyDate: values.policyPremiumFrequencyDate,
          policyOrderNumber: values.policyOrderNumber,
          coverageOrderNumber: values.coverageOrderNumber,
        };

        const addCompanyResponse = await newPolicy(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          navigate(`/profile/customer/${customerId}`);
          resetForm();
          setSelectedCategoryId(null);
          setSelectedSubCategoryId(null);
          setSelectedSubToSubCategoryId(null);
          setSelectedSubToSubToSubCategoryId(null);
        }
      } catch (error) {
        showErrorMessage("policy number unique");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.policyNumber !== "" &&
      formik.values.policyNumber !== undefined &&
      formik.values.policyCustomerId !== "" &&
      formik.values.policyCustomerId !== undefined
    );
  }, [formik.values.policyNumber, formik.values.policyCustomerId]);

  const handelColose = () => {
    navigate(`/profile/customer/${customerId}`);
  };

  useEffect(() => {
    if (customer) {
      setCustomerName(
        `${customer.customerFirstName || ""} ${
          customer.customerLastName || ""
        }`.trim()
      );
    }
  }, [customer]);

  if (isLoading || countDataIsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">
        Error fetching customer data. Please try again later.
      </Typography>
    );
  }

  if (!customer) {
    return <Typography>No customer data available.</Typography>;
  }

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Policy Holder Details
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    {/* <Grid item lg={7} md={12} sm={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={4} md={4} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Policy Holders Name
                              </Typography>
                            </Grid>
                            <Grid item lg={8} md={8} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                            {
                                  (customer.customerFirstName || "") +
                                  " " +
                                  (customer.customerLastName || "")
                                }
                              </Typography>
                              <TextField
                                size="small"
                                value={
                                  (customer.customerFirstName || "") +
                                  " " +
                                  (customer.customerLastName || "")
                                }
                                className={classes.textField}
                                InputProps={{
                                  readOnly: true,
                                  // startAdornment: (
                                  //   <InputAdornment position= "end">
                                  //     <CiSearch
                                  //       className={classes.searchWidgetIcon}
                                  //     />
                                  //   </InputAdornment>
                                  // ),
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid> */}

                    {customerName && !selectedId && (
                      <>
                        {/* <Grid item lg={1} md={1}></Grid> */}
                        <Grid item lg={7} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Grid
                                className={classes.profileBottomDwon23}
                                onClick={() =>
                                  navigate(`/profile/customer/${customer?.id}`)
                                }
                                sx={{
                                  cursor: "pointer",
                                }}
                              >
                                {customer ? (
                                  customer.customerImg ? (
                                    <img
                                      src={customer.customerImg}
                                      alt="Profile"
                                      className={classes.profileImage}
                                    />
                                  ) : (
                                    <Avatar
                                      alt="Travis Howard"
                                      src={AvatarImg}
                                      className={classes.profileImage}
                                    />
                                  )
                                ) : (
                                  <Avatar
                                    alt="Travis Howard"
                                    src={AvatarImg}
                                    className={classes.profileImage}
                                  />
                                )}
                              </Grid>
                            </Grid>
                            <Grid item>
                              <Grid className={classes.profileDetails}>
                                <Link
                                  to={`/profile/customer/${customer?.id}`}
                                  className={classes.iconLinkPropNormal}
                                >
                                  <Typography
                                    variant="h4"
                                    className={classes.profileDetailItem}
                                  >
                                    <span
                                      style={{
                                        cursor: "pointer",
                                        // textDecoration: "underline",
                                        // color: "#dae4f0",
                                      }}
                                      onClick={() =>
                                        navigate(
                                          `/profile/customer/${customer?.id}`
                                        )
                                      }
                                    >
                                      {(customer?.customerFirstName || "") +
                                        " " +
                                        (customer?.customerLastName || "")}
                                      <span
                                        style={{
                                          cursor: "pointer",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          paddingLeft: "3px",
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faUser}
                                          style={{ color: "#1976d2" }}
                                        />
                                      </span>
                                    </span>
                                  </Typography>
                                </Link>
                                {customer?.customerMobileNum && (
                                  <Typography
                                    variant="body1"
                                    className={classes.contactInfo}
                                  >
                                    <span className={classes.iconText}>
                                      <PhoneIcon />{" "}
                                      {customer?.customerMobileNum}
                                    </span>
                                  </Typography>
                                )}
                                {customer?.customerEmail && (
                                  <Typography
                                    variant="body1"
                                    className={classes.contactInfo}
                                  >
                                    <span className={classes.iconText}>
                                      <EmailIcon /> {customer?.customerEmail}
                                    </span>
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    <Grid item lg={5} md={12} sm={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={4} md={4} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Order Number
                              </Typography>
                            </Grid>
                            <Grid item lg={8} md={8} sm={12} xs={12}>
                              <TextField
                                placeholder="Order Number"
                                size="small"
                                {...formik.getFieldProps("policyOrderNumber")}
                                className={classes.textField}
                                type="number"
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              {!selectedId && (
                <>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Policy Details
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={5} p={"24px"}>
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <Grid container spacing={3}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Policy No
                                  </Typography>
                                </Grid>
                                <Grid item lg={8} md={8} sm={12} xs={12}>
                                  <TextField
                                    placeholder="Policy No"
                                    size="small"
                                    {...formik.getFieldProps("policyNumber")}
                                    className={classes.textField}
                                    InputProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Insurance Company
                                  </Typography>
                                </Grid>
                                <Grid item lg={6.5} md={6.5} sm={10} xs={10}>
                                  <Autocomplete
                                    options={brandOptions}
                                    loading={brandIsLoading}
                                    getOptionLabel={(option: CustomerOption) =>
                                      option.label
                                    }
                                    value={
                                      brandOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          Number(
                                            formik.values
                                              .policyInsuranceCompanyId
                                          )
                                      ) || null
                                    }
                                    onChange={(
                                      _,
                                      newValue: CustomerOption | null
                                    ) => {
                                      formik.setFieldValue(
                                        "policyInsuranceCompanyId",
                                        newValue?.value || ""
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Insurance Company"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        className="textField"
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {brandIsLoading ? (
                                                <CircularProgress size={20} />
                                              ) : null}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    sx={{ width: "100%" }}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  lg={1.5}
                                  md={1.5}
                                  sm={2}
                                  xs={2}
                                  display="flex"
                                  sx={{
                                    justifyContent: "flex-end",
                                    alignItems: "flex-end",
                                    textAlign: "end",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    className={classes.editButton}
                                    onClick={() => {
                                      setOpenProductBrand(true);
                                    }}
                                  >
                                    <AddIcon />
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Joint Policy Owner
                                  </Typography>
                                </Grid>
                                <Grid item lg={6.5} md={6.5} sm={10} xs={10}>
                                  <Autocomplete
                                    options={relationOptions}
                                    loading={relationIsLoading}
                                    getOptionLabel={(option: CustomerOption) =>
                                      option.label
                                    }
                                    value={
                                      relationOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          Number(
                                            formik.values.jointPolicyOwnerId
                                          )
                                      ) || null
                                    }
                                    onChange={(
                                      _,
                                      newValue: CustomerOption | null
                                    ) => {
                                      formik.setFieldValue(
                                        "jointPolicyOwnerId",
                                        newValue?.value || ""
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Joint Policy Owner"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        className="textField"
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {relationIsLoading ? (
                                                <CircularProgress size={20} />
                                              ) : null}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    sx={{ width: "100%" }}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  lg={1.5}
                                  md={1.5}
                                  sm={2}
                                  xs={2}
                                  display="flex"
                                  sx={{
                                    justifyContent: "flex-end",
                                    alignItems: "flex-end",
                                    textAlign: "end",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    className={classes.editButton}
                                    onClick={handleOpenDialogRelative}
                                  >
                                    <AddIcon />
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <Grid container spacing={3}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Contengent Policy Owner
                                  </Typography>
                                </Grid>
                                <Grid item lg={6.5} md={6.5} sm={10} xs={10}>
                                  <Autocomplete
                                    options={relationOptions}
                                    loading={relationIsLoading}
                                    getOptionLabel={(option: CustomerOption) =>
                                      option.label
                                    }
                                    value={
                                      relationOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          Number(
                                            formik.values
                                              .contengentPolicyOwnerId
                                          )
                                      ) || null
                                    }
                                    onChange={(
                                      _,
                                      newValue: CustomerOption | null
                                    ) => {
                                      formik.setFieldValue(
                                        "contengentPolicyOwnerId",
                                        newValue?.value || ""
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Contingent Policy Owner"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        className="textField"
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {relationIsLoading ? (
                                                <CircularProgress size={20} />
                                              ) : null}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    sx={{ width: "100%" }}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  lg={1.5}
                                  md={1.5}
                                  sm={2}
                                  xs={2}
                                  display="flex"
                                  sx={{
                                    justifyContent: "flex-end",
                                    alignItems: "flex-end",
                                    textAlign: "end",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    className={classes.editButton}
                                    onClick={handleOpenDialogRelative}
                                  >
                                    <AddIcon />
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Issue Date
                                  </Typography>
                                </Grid>
                                <Grid item lg={8} md={8} sm={12} xs={12}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                  >
                                    <DatePicker
                                      value={formik.values.policyIssueDate}
                                      onChange={(date) =>
                                        formik.setFieldValue(
                                          "policyIssueDate",
                                          date
                                        )
                                      }
                                      slotProps={{
                                        textField: {
                                          size: "small",
                                          sx: { width: "100%" },
                                          InputProps: {
                                            sx: { fontSize: 14 },
                                          },
                                          InputLabelProps: {
                                            sx: { fontSize: 14 },
                                          },
                                        },
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Premium Details
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={5} p={"24px"}>
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <Grid container spacing={3}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Payor
                                  </Typography>
                                </Grid>
                                <Grid item lg={8} md={8} sm={12} xs={12}>
                                  <TextField
                                    placeholder="Payor"
                                    size="small"
                                    {...formik.getFieldProps("policyPayor")}
                                    className={classes.textField}
                                    InputProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Premium
                                  </Typography>
                                </Grid>
                                <Grid item lg={8} md={8} sm={12} xs={12}>
                                  <TextField
                                    placeholder="Premium"
                                    size="small"
                                    type="number"
                                    {...formik.getFieldProps("policyPremium")}
                                    className={classes.textField}
                                    InputProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <Grid container spacing={3}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Premium Frequency
                                  </Typography>
                                </Grid>
                                <Grid item lg={8} md={8} sm={12} xs={12}>
                                  <Grid container spacing={2}>
                                    <Grid item lg={12} md={12} sm={12} xs={12}>
                                      <Autocomplete
                                        options={premiumFrequencyOptions}
                                        getOptionLabel={(
                                          option: CustomerOptionString
                                        ) => option.label}
                                        value={
                                          premiumFrequencyOptions.find(
                                            (opt) =>
                                              opt.value ===
                                              String(
                                                formik.values
                                                  .policyPremiumFrequency
                                              )
                                          ) || null
                                        }
                                        onChange={(
                                          _,
                                          newValue: CustomerOptionString | null
                                        ) => {
                                          const selectedValue =
                                            newValue?.value || "";
                                          formik.setFieldValue(
                                            "policyPremiumFrequency",
                                            selectedValue
                                          );

                                          // Reset policyExpiryDate unless "Annually" or "Custom"
                                          if (
                                            selectedValue !== "Annually" &&
                                            selectedValue !== "Custom" &&
                                            selectedValue !== "OneTime"
                                          ) {
                                            formik.setFieldValue(
                                              "policyPremiumFrequencyDate",
                                              null
                                            );
                                          }
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="Draw Day"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            className="textField"
                                          />
                                        )}
                                        sx={{ width: "100%" }}
                                      />
                                    </Grid>
                                    {(formik.values.policyPremiumFrequency ===
                                      "Annually" ||
                                      formik.values.policyPremiumFrequency ===
                                        "Custom" ||
                                      formik.values.policyPremiumFrequency ===
                                        "OneTime") && (
                                      <Grid
                                        item
                                        lg={12}
                                        md={12}
                                        sm={12}
                                        xs={12}
                                      >
                                        <LocalizationProvider
                                          dateAdapter={AdapterDayjs}
                                        >
                                          <DatePicker
                                            value={
                                              formik.values
                                                .policyPremiumFrequencyDate
                                            }
                                            onChange={(date) =>
                                              formik.setFieldValue(
                                                "policyPremiumFrequencyDate",
                                                date
                                              )
                                            }
                                            slotProps={{
                                              textField: {
                                                size: "small",
                                                sx: { width: "100%" },
                                                InputProps: {
                                                  sx: { fontSize: 14 },
                                                },
                                                InputLabelProps: {
                                                  sx: { fontSize: 14 },
                                                },
                                              },
                                            }}
                                          />
                                        </LocalizationProvider>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={4} md={4} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Draw Day
                                  </Typography>
                                </Grid>
                                <Grid item lg={8} md={8} sm={12} xs={12}>
                                  <Autocomplete
                                    options={dayOptions}
                                    getOptionLabel={(
                                      option: CustomerOptionString
                                    ) => option.label}
                                    value={
                                      dayOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          String(formik.values.policyDrawDay)
                                      ) || null
                                    }
                                    onChange={(
                                      _,
                                      newValue: CustomerOptionString | null
                                    ) => {
                                      formik.setFieldValue(
                                        "policyDrawDay",
                                        newValue?.value || ""
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Draw Day"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        className="textField"
                                      />
                                    )}
                                    sx={{ width: "100%" }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </>
              )}
              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => handelColose()}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={<BiSolidMessageSquareAdd />}
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || dataIsLoading}
                      >
                        Create
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {openProductBrand && (
          <NewProductBrand
            handleCloseDialog={() => setOpenProductBrand(false)}
            openModel={openProductBrand}
          />
        )}

        {openAddProject && (
          <NewPopUpRelative
            openModel={openAddProject}
            handleCloseDialog={handleCloseDialogRelative}
            customerClientId={String(customerId)}
          />
        )}
      </form>
    </div>
  );
};

export default Index;
